import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCurrentUserData } from "../store/users";
import localStorageService from "../service/http.localStorage";
import { useI18n } from "../i18n/LanguageContext";

function NavProfile() {
  const currentUser = useSelector(getCurrentUserData());
  const [isOpen, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const location = useLocation();
  // Извлекаем country из пути /kz/login -> kz
  const getCountryFromPath = () => {
    const segments = location.pathname.split("/").filter(Boolean);
    return segments[0] || null; // первый сегмент пути
  };

  const country = getCountryFromPath();
  const { t } = useI18n();

  // Закрытие меню при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setOpen((prevState) => !prevState);
  };

  const handleClear = () => {
    localStorageService.removeAuthData();
  };

  if (!currentUser) {
    return (
      <Link to="/login">
        <button
          className="px-5 py-3 rounded-lg bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-400 hover:to-green-400 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          onClick={handleClear}
        >
          {t("nav.login")}
        </button>
      </Link>
    );
  }

  return (
    <div className="relative inline-block text-left">
      {/* Аватар/кнопка пользователя */}
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-lime-500 to-green-500 hover:from-lime-400 hover:to-green-400 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 border border-lime-600"
        onClick={toggleMenu}
      >
        {/* Аватар пользователя */}
        {/* <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-lime-600 font-bold text-sm">
          {currentUser.nickname?.charAt(0).toUpperCase() || "U"}
        </div> */}
        <span className="max-w-24 truncate">{currentUser.nickname}</span>
        {/* Анимированная стрелка */}
        <svg
          className={`w-4 h-4 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Выпадающее меню с анимацией */}
      <div
        ref={menuRef}
        className={`absolute right-0 z-50 w-48 mt-2 origin-top-right bg-white rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 overflow-hidden transition-all duration-300 transform ${
          isOpen
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
        }`}
      >
        {/* Заголовок профиля */}
        <div className="p-4 bg-gradient-to-r from-gray-50 to-lime-50 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-lime-500 to-green-500 flex items-center justify-center text-white font-bold">
              {currentUser.nickname?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {currentUser.nickname}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {currentUser.email}
              </p>
            </div>
          </div>
        </div>

        <div className="py-2">
          {currentUser?.isVerify && (
            <Link
              to={`${country}/user/${currentUser._id}`}
              className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-lime-50 hover:text-lime-700 transition-all duration-200 group"
              onClick={toggleMenu}
            >
              <svg
                className="w-4 h-4 text-gray-400 group-hover:text-lime-500 transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {t("nav.profile")}
            </Link>
          )}

          {/* Разделитель */}
          <div className="border-t border-gray-100 my-1"></div>

          <Link
            to={`${country}/logout`}
            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200 group"
            onClick={toggleMenu}
          >
            <svg
              className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {t("nav.logout")}
          </Link>
        </div>

        {/* Статус верификации */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">{t("nav.status")}</span>
            <span
              className={`px-2 py-1 rounded-full font-medium ${
                currentUser.isVerify
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {currentUser.isVerify ? t("nav.verified") : t("nav.pending")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NavProfile;
